(() => {
  const VERSION = 'v2';
  const PREFIX = `vanscape:personalizer:${VERSION}`;
  const JOB_PREFIX = `${PREFIX}:job:`;
  const ACTIVE_PREFIX = `${PREFIX}:active:`;
  const JOB_TTL_MS = 21 * 24 * 60 * 60 * 1000;

  function normalize(value) {
    return String(value ?? '').trim().toLowerCase();
  }

  function storageGet(storage, key) {
    try {
      return storage.getItem(key);
    } catch {
      return null;
    }
  }

  function storageSet(storage, key, value) {
    try {
      storage.setItem(key, value);
      return true;
    } catch {
      return false;
    }
  }

  function storageRemove(storage, key) {
    try {
      storage.removeItem(key);
    } catch {}
  }

  function parseJson(raw) {
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  function normalizeContext(context = {}) {
    return {
      product_id: String(context.product_id || ''),
      product_handle: normalize(context.product_handle),
      style: normalize(context.style)
    };
  }

  function contextKey(context = {}) {
    const normalized = normalizeContext(context);
    const productKey = normalized.product_id || normalized.product_handle;
    return encodeURIComponent([productKey, normalized.style].join(':'));
  }

  function jobKey(jobId) {
    return `${JOB_PREFIX}${String(jobId || '')}`;
  }

  function activeKey(context) {
    return `${ACTIVE_PREFIX}${contextKey(context)}`;
  }

  function sameContext(record, context) {
    if (!record) return false;
    const expected = normalizeContext(context);
    const actual = normalizeContext(record);
    const sameProduct = expected.product_id
      ? actual.product_id === expected.product_id
      : actual.product_handle === expected.product_handle;
    return Boolean(sameProduct && actual.style === expected.style);
  }

  function isExpired(record, now = Date.now()) {
    const timestamp = Number(record?.updated_at || record?.created_at || 0);
    return !timestamp || now - timestamp > JOB_TTL_MS;
  }

  function get(jobId) {
    if (!jobId) return null;
    const record = parseJson(storageGet(localStorage, jobKey(jobId)));
    if (!record || isExpired(record)) {
      remove(jobId);
      return null;
    }
    return record;
  }

  function save(record) {
    if (!record?.job_id) return null;
    const now = Date.now();
    const normalizedContext = normalizeContext(record);
    const next = {
      ...record,
      ...normalizedContext,
      job_id: String(record.job_id),
      created_at: Number(record.created_at || now),
      updated_at: now
    };
    return storageSet(localStorage, jobKey(next.job_id), JSON.stringify(next)) ? next : null;
  }

  function create(context, data = {}) {
    const normalizedContext = normalizeContext(context);
    return save({
      ...data,
      ...normalizedContext,
      job_id: String(data.job_id || ''),
      status: data.status || 'queued',
      created_at: Date.now()
    });
  }

  function update(jobId, patch = {}) {
    const current = get(jobId);
    if (!current) return null;
    const selection = patch.selection
      ? { ...(current.selection || {}), ...patch.selection }
      : current.selection;
    return save({
      ...current,
      ...patch,
      selection
    });
  }

  function setActive(context, jobId) {
    const record = get(jobId);
    if (!record || !sameContext(record, context)) return false;
    return storageSet(sessionStorage, activeKey(context), record.job_id);
  }

  function findLatest(context) {
    const matches = [];
    try {
      for (let index = 0; index < localStorage.length; index += 1) {
        const key = localStorage.key(index);
        if (!key?.startsWith(JOB_PREFIX)) continue;
        const record = parseJson(storageGet(localStorage, key));
        if (!record || isExpired(record)) {
          if (record?.job_id) remove(record.job_id);
          continue;
        }
        if (sameContext(record, context) && !record.discarded) matches.push(record);
      }
    } catch {
      return null;
    }
    return matches.sort((a, b) => Number(b.updated_at || 0) - Number(a.updated_at || 0))[0] || null;
  }

  function getActive(context, { allowLatest = true } = {}) {
    const key = activeKey(context);
    const activeId = storageGet(sessionStorage, key);
    const activeRecord = get(activeId);
    if (activeRecord && sameContext(activeRecord, context)) return activeRecord;
    if (activeId) storageRemove(sessionStorage, key);
    if (!allowLatest) return null;
    const latest = findLatest(context);
    if (latest) setActive(context, latest.job_id);
    return latest;
  }

  function clearActive(context, { removeRecord = false } = {}) {
    const key = activeKey(context);
    const activeId = storageGet(sessionStorage, key);
    storageRemove(sessionStorage, key);
    if (removeRecord && activeId) remove(activeId);
  }

  function remove(jobId) {
    if (!jobId) return;
    storageRemove(localStorage, jobKey(jobId));
  }

  function garbageCollect() {
    const now = Date.now();
    try {
      const expiredKeys = [];
      for (let index = 0; index < localStorage.length; index += 1) {
        const key = localStorage.key(index);
        if (!key?.startsWith(JOB_PREFIX)) continue;
        const record = parseJson(storageGet(localStorage, key));
        if (!record || isExpired(record, now)) expiredKeys.push(key);
      }
      expiredKeys.forEach((key) => storageRemove(localStorage, key));
    } catch {}
  }

  garbageCollect();

  window.VanscapePersonalizerJobStore = Object.freeze({
    normalizeContext,
    contextKey,
    sameContext,
    get,
    create,
    update,
    save,
    setActive,
    getActive,
    clearActive,
    remove,
    garbageCollect
  });
})();
